export const CHECKOUT_ADAPTER_STOREFRONT = 'storefront'
export const CHECKOUT_ADAPTER_RWD = 'rwd'
export const CHECKOUT_ADAPTER_PENDING = 'pending'
export const CHECKOUT_ADAPTER_NONE = 'none'

function normalizeMode(value) {
  const mode = String(value ?? '').trim().toLowerCase()

  return [CHECKOUT_ADAPTER_STOREFRONT, CHECKOUT_ADAPTER_RWD].includes(mode) ? mode : ''
}

function normalizeBaseUrl(value) {
  return String(value ?? '').trim().replace(/\/+$/, '')
}

export function isSupportedCheckoutPath(pathname) {
  return /(?:^|\/)basket(?:\/|$)/.test(String(pathname ?? '').split(/[?#]/, 1)[0])
}

export function buildCheckoutConfigUrl(appUrl, shopId) {
  const baseUrl = normalizeBaseUrl(appUrl)
  if (baseUrl === '') {
    return ''
  }

  const url = new URL(`${baseUrl}/checkout-config`)
  const normalizedShopId = String(shopId ?? '').trim()
  if (normalizedShopId !== '') {
    url.searchParams.set('shop', normalizedShopId)
  }

  return url.toString()
}

export function readCheckoutBootstrap({ window: windowObject = globalThis.window, document: documentObject = globalThis.document } = {}) {
  const element = documentObject?.getElementById?.('octolize-dhl-bootstrap') ?? null
  const globalConfig = windowObject?.octolizeDhl ?? {}
  const appUrl = normalizeBaseUrl(element?.dataset?.appUrl ?? globalConfig.appUrl)
  const shopId = String(element?.dataset?.shopId ?? globalConfig.shopId ?? '').trim()
  const mode = normalizeMode(
    element?.dataset?.checkoutMode ?? element?.dataset?.adapter ?? globalConfig.checkoutMode ?? globalConfig.adapter,
  )

  return {
    appUrl,
    shopId,
    mode,
    configUrl: String(element?.dataset?.checkoutConfigUrl ?? globalConfig.checkoutConfigUrl ?? '').trim()
      || buildCheckoutConfigUrl(appUrl, shopId),
  }
}

export function selectCheckoutAdapter(bootstrap, { window: windowObject = globalThis.window } = {}) {
  if (!isSupportedCheckoutPath(windowObject?.location?.pathname)) {
    return CHECKOUT_ADAPTER_NONE
  }

  if (bootstrap?.mode === CHECKOUT_ADAPTER_RWD || bootstrap?.mode === CHECKOUT_ADAPTER_STOREFRONT) {
    return bootstrap.mode
  }

  return typeof windowObject?.useStorefront === 'function'
    ? CHECKOUT_ADAPTER_STOREFRONT
    : CHECKOUT_ADAPTER_PENDING
}

export function createCheckoutConfigLoader({
  fetch: fetchImpl = globalThis.fetch,
  url,
  logger = console,
  timeoutMs = 8000,
} = {}) {
  let request = null
  let value

  return {
    async load() {
      if (value !== undefined) {
        return value
      }
      if (request) {
        return request
      }
      if (typeof fetchImpl !== 'function' || String(url ?? '').trim() === '') {
        return null
      }

      const abortController = typeof AbortController === 'function' ? new AbortController() : null
      let timeoutId
      const timeout = new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => {
          abortController?.abort()
          reject(new Error('Checkout config request timed out'))
        }, timeoutMs)
      })
      const fetchRequest = fetchImpl(url, {
        mode: 'cors',
        credentials: 'omit',
        ...(abortController ? { signal: abortController.signal } : {}),
      })

      request = Promise.race([fetchRequest, timeout])
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Checkout config request failed with status ${response.status}`)
          }
          return response.json()
        })
        .then((payload) => {
          value = payload
          return payload
        })
        .catch((error) => {
          logger?.warn?.('Cannot load DHL checkout configuration', error)
          return null
        })
        .finally(() => {
          clearTimeout(timeoutId)
          request = null
        })

      return request
    },
    reset() {
      request = null
      value = undefined
    },
  }
}
